<template>
  <div class="collection-detail-container">
    <div v-if="isLoading" class="loading-state">
      <p>{{ t('collections.detail.loading') }}</p>
      <div class="spinner"></div>
    </div>

    <div v-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <router-link to="/collections" class="btn btn-secondary">{{ t('collections.shared.backToList') }}</router-link>
    </div>

    <div v-if="!isLoading && !error && collection" class="collection-content">
      <div class="collection-header">
        <h1>{{ t('collections.detail.title', { name: collection.name }) }}</h1>
        <router-link :to="`/collections/edit/${collectionId}`" class="btn btn-primary btn-manage-assets">
          {{ t('collections.detail.buttons.manage') }}
        </router-link>
      </div>

      <div class="assets-section">
        <h2>{{ t('collections.detail.assets.heading', { count: collection.assetCount }) }}</h2>
        <div v-if="collection.assets && collection.assets.length > 0" class="assets-list">
          <table>
            <thead>
              <tr>
                <th>{{ t('collections.shared.columns.code') }}</th>
                <th>{{ t('collections.shared.columns.name') }}</th>
                <th>{{ t('collections.shared.columns.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="asset in collection.assets" :key="asset.code">
                <td>{{ asset.code }}</td>
                <td>{{ asset.name }}</td>
                <td class="asset-actions">
                  <button @click="confirmRemoveAsset(asset.code)" class="btn btn-sm btn-danger">
                    {{ t('collections.shared.actions.remove') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-assets-state">
          <p>{{ t('collections.detail.empty.title') }}</p>
          <p>{{ t('collections.detail.empty.prompt') }}</p>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && !error && !collection" class="not-found-state">
      <p>{{ t('collections.detail.notFound') }}</p>
      <router-link to="/collections" class="btn btn-secondary">{{ t('collections.shared.backToList') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
    getCollectionDetails as apiGetCollectionDetails,
    removeAssetFromCollection as apiRemoveAssetFromCollection
} from '@/services/api.js';

/**
 * @file CollectionDetailPage.vue
 * @description Page component for displaying the detailed view of a single asset collection.
 * It shows the collection's name, a list of its assets, and allows users to manage assets
 * (e.g., remove assets, navigate to add/manage more).
 */

const route = useRoute();

/**
 * Reactive ref holding the ID of the current collection, extracted from route parameters.
 * @type {import('vue').Ref<string|null>}
 */
const collectionId = ref(route.params.id);

/**
 * Reactive ref holding the collection object fetched from the API.
 * Includes collection details and an array of its assets.
 * @type {import('vue').Ref<object|null>}
 */
const collection = ref(null);

/**
 * Reactive boolean to indicate if collection data is currently being loaded.
 * @type {import('vue').Ref<boolean>}
 */
const isLoading = ref(false);

/**
 * Reactive ref to store any error message that occurs during API calls.
 * @type {import('vue').Ref<string|null>}
 */
const error = ref(null);
const { t } = useI18n();

/**
 * Fetches detailed information for the current collection from the API (mocked)
 * using the `collectionId` from the route. Updates reactive state for loading,
 * error, and the fetched collection data.
 * @async
 */
const fetchCollectionDetails = async () => {
  if (!collectionId.value) {
    error.value = t('collections.detail.errors.missingId');
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  error.value = null;
  try {
    const response = await apiGetCollectionDetails(collectionId.value);
    collection.value = response; // Assuming response is the collection object with detailed assets
  } catch (err) {
    console.error(`Error fetching details for collection ${collectionId.value}:`, err);
    const reason = err?.message || t('common.unknownError');
    error.value = t('collections.detail.errors.loadFailed', { reason });
    collection.value = null; // Clear collection if fetch failed
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handles the removal of a specific asset from the current collection
 * after user confirmation via a dialog. Updates the local collection data on success.
 * @async
 * @param {string} assetCode - The unique code of the asset to be removed.
 */
const confirmRemoveAsset = async (assetCode) => {
  if (window.confirm(t('collections.detail.prompts.removeAsset', { code: assetCode }))) {
    isLoading.value = true; // Optional: show loading indicator during the delete operation
    try {
      const response = await apiRemoveAssetFromCollection(collectionId.value, assetCode);
      if (response.success) {
        // Update local collection data by filtering out the removed asset
        if (collection.value && collection.value.assets) {
          collection.value.assets = collection.value.assets.filter(asset => asset.code !== assetCode);
          // Update assetCount if it's part of the local collection object
          if (typeof collection.value.assetCount === 'number' && collection.value.assetCount > 0) {
            collection.value.assetCount--;
          }
        }
        alert(t('collections.detail.messages.removeSuccess', { code: assetCode }));
        // Alternatively, to ensure full consistency, re-fetch the collection details:
        // await fetchCollectionDetails();
      } else {
        throw new Error(response.message || t('collections.detail.errors.removeUnknown', { code: assetCode }));
      }
    } catch (err) {
      console.error(`Error removing asset ${assetCode} from collection ${collectionId.value}:`, err);
      const reason = err?.message || t('common.unknownError');
      error.value = t('collections.detail.errors.removeFailed', { reason });
      alert(error.value); // Also show an alert for immediate feedback
    } finally {
      isLoading.value = false;
    }
  }
};

// Fetches the collection details when the component is first mounted.
onMounted(() => {
  fetchCollectionDetails();
});
</script>

<style scoped>
.collection-detail-container {
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  margin-bottom: 10px;
  color: #333;
}
.collection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.btn {
  padding: 8px 15px;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.9em;
  border: 1px solid transparent;
}
.btn-primary {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}
.btn-primary:hover {
  background-color: #0056b3;
}
.btn-secondary {
  background-color: #6c757d;
  color: white;
}
.btn-danger {
  background-color: #dc3545;
  color: white;
}
.btn-sm {
  padding: 5px 10px;
  font-size: 0.8em;
}

.loading-state, .error-state, .not-found-state, .empty-assets-state {
  text-align: center;
  padding: 40px 0;
  color: #555;
}

.error-message {
  color: #dc3545;
  margin-bottom: 15px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #007bff;
  animation: spin 1s ease infinite;
  margin: 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.assets-section h2 {
  margin-bottom: 15px;
  color: #444;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.assets-list table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.assets-list th, .assets-list td {
  border: 1px solid #ddd;
  padding: 10px 12px;
  text-align: left;
}

.assets-list th {
  background-color: #f8f9fa;
  font-weight: bold;
}

.assets-list tr:nth-child(even) {
  background-color: #f9f9f9;
}
.assets-list tr:hover {
  background-color: #f1f1f1;
}

.asset-actions .btn-danger {
  /* No extra margin needed if it's the only button */
}

.empty-assets-state p {
  margin-bottom: 10px;
}
</style>
